<script lang="ts">
/**
 * Four snaps, from the header alone (the map behind stays fully usable) to
 * full screen, through two resting heights sized against the parent. The
 * expanded one is the reading snap: a pull on the content brings the sheet up
 * to it, and only from there does the content scroll on its own.
 */
export type BottomSheetPosition = 'collapsed' | 'intermediate' | 'expanded' | 'full';

export const BOTTOM_SHEET_INTERMEDIATE_RATIO = 0.4;
const BOTTOM_SHEET_EXPANDED_RATIO = 0.75;
</script>

<script setup lang="ts">
import { computed, onActivated, onBeforeUnmount, onDeactivated, onMounted, ref, watch } from 'vue';
import { resolveSnap, type Snap } from '@/libs/helpers/bottom-sheet-snap';
import { useAppStore } from '@/stores/app';

const props = withDefaults(
  defineProps<{
    /**
     * Target height of the intermediate snap point, as a ratio of the parent element's height.
     * A floor rather than an absolute height: the snap never goes below the collapsed snap (the
     * header and footer slots), nor above the full height (see `intermediateHeight`). A caller
     * anchoring a layout above the sheet must not derive it from this ratio — it is only a floor
     * — but listen to `update:intermediateHeight`, which carries the measured snap.
     */
    intermediateRatio?: number;
    /** Target height of the expanded snap point, floored the same way by the intermediate one. */
    expandedRatio?: number;
  }>(),
  { intermediateRatio: BOTTOM_SHEET_INTERMEDIATE_RATIO, expandedRatio: BOTTOM_SHEET_EXPANDED_RATIO },
);

const emit = defineEmits<{
  /** Measured height of the intermediate snap, for anchoring a layout above the sheet. */
  'update:intermediateHeight': [height: number];
  /**
   * Live height of the sheet, every frame of a drag included, for a layout that follows
   * the sheet. `dragging` tells the caller to drop its own transition, as the sheet does.
   */
  'update:height': [height: number, dragging: boolean];
}>();

const position = defineModel<BottomSheetPosition>('position', { default: 'intermediate' });

const appStore = useAppStore();

const sheetEl = ref<HTMLElement>();
const contentEl = ref<HTMLElement>();
const headerEl = ref<HTMLElement>();
const footerEl = ref<HTMLElement>();

// The sheet is anchored to the bottom of its offset parent: the parent defines
// the sheet's playground (and therefore handles safe areas), keeping this
// component agnostic of the page layout and of the map implementation.
const parentHeight = ref<number>(0);
const headerHeight = ref<number>(0);
const footerHeight = ref<number>(0);
const sheetHeight = ref<number>(0);
const isDragging = ref<boolean>(false);
const isContentScrolled = ref<boolean>(false);

const fullHeight = computed<number>(() => parentHeight.value);

// Neither slot can shrink or scroll (`flex-shrink: 0` inside an `overflow: hidden`
// sheet): collapsed is exactly the two of them, and each ratio is a floor above
// the snap below it — on a short viewport (a small phone, any device in
// landscape) a purely proportional snap would clip content the passenger cannot
// reach without opening the sheet full screen.
const collapsedHeight = computed<number>(() =>
  Math.min(headerHeight.value + footerHeight.value, fullHeight.value),
);
const intermediateHeight = computed<number>(() =>
  Math.min(Math.max(parentHeight.value * props.intermediateRatio, collapsedHeight.value), fullHeight.value),
);
const expandedHeight = computed<number>(() =>
  Math.min(Math.max(parentHeight.value * props.expandedRatio, intermediateHeight.value), fullHeight.value),
);

const snapHeights = computed<Record<BottomSheetPosition, number>>(() => ({
  collapsed: collapsedHeight.value,
  intermediate: intermediateHeight.value,
  expanded: expandedHeight.value,
  full: fullHeight.value,
}));

const snaps = computed<Snap<BottomSheetPosition>[]>(() =>
  (['collapsed', 'intermediate', 'expanded', 'full'] as const).map(key => ({
    key,
    height: snapHeights.value[key],
  })),
);

watch([position, snapHeights], () => {
  if (!isDragging.value) sheetHeight.value = snapHeights.value[position.value];
});

// Nothing to anchor on before the first measurement: the caller keeps its own
// fallback until a real height comes through.
watch(intermediateHeight, height => {
  if (height > 0) emit('update:intermediateHeight', height);
});

watch([sheetHeight, isDragging], ([height, dragging]) => {
  if (height > 0) emit('update:height', height, dragging);
});

function onContentScroll() {
  isContentScrolled.value = (contentEl.value?.scrollTop ?? 0) > 0;
}

// #region Drag gesture
// The first touchmove of a gesture is often a jitter of a pixel or two: reading
// the direction from it dropped pull-downs that never got a second chance.
const DIRECTION_DEAD_ZONE = 4;
// A finger held still before lifting is not a flick, however fast it moved before.
const STALE_VELOCITY_DELAY = 100;

let dragStartX = 0;
let dragStartY = 0;
let dragStartHeight = 0;
let dragStartPosition: BottomSheetPosition = 'intermediate';
// Ceiling of the current gesture: a pull on the content stops at the reading snap.
let dragMaxHeight = 0;
let lastMoveY = 0;
let lastMoveTime = 0;
let velocity = 0;
// What the current touch sequence drives once its direction is known: the
// sheet, or the browser's own scrolling of the content.
let touchGesture: 'pending' | 'sheet' | 'native' = 'pending';
// Tears down the window listeners of a mouse drag, from wherever it ends.
let releasePointerDrag: (() => void) | undefined;

// Below the reading snap a pull on the content raises the sheet instead of
// scrolling it. `touch-action` follows (see the template) so the browser never
// starts a native scroll, which would make the following moves uncancelable.
const contentPullsSheet = computed<boolean>(() => snapHeights.value[position.value] < expandedHeight.value);

function startDrag(y: number) {
  isDragging.value = true;
  dragStartY = y;
  dragStartHeight = sheetHeight.value;
  dragStartPosition = position.value;
  dragMaxHeight = fullHeight.value;
  lastMoveY = y;
  lastMoveTime = performance.now();
  velocity = 0;
}

function moveDrag(y: number) {
  const now = performance.now();
  if (now > lastMoveTime) {
    // Smoothed: one jittery frame at the end of the gesture must not decide the snap.
    const instantVelocity = (y - lastMoveY) / (now - lastMoveTime);
    velocity = velocity === 0 ? instantVelocity : instantVelocity * 0.7 + velocity * 0.3;
  }
  lastMoveY = y;
  lastMoveTime = now;
  const newHeight = dragStartHeight + (dragStartY - y);
  sheetHeight.value = Math.min(Math.max(newHeight, collapsedHeight.value), dragMaxHeight);
}

function endDrag() {
  if (!isDragging.value) return;
  isDragging.value = false;
  if (performance.now() - lastMoveTime > STALE_VELOCITY_DELAY) velocity = 0;

  const target = resolveSnap({
    snaps: snaps.value.filter(snap => snap.height <= dragMaxHeight),
    start: { key: dragStartPosition, height: dragStartHeight },
    endHeight: sheetHeight.value,
    velocity,
  });
  position.value = target;
  sheetHeight.value = snapHeights.value[target];
}

function onHeaderPointerDown(event: PointerEvent) {
  // Touch drags are handled by the touch listeners below (they need
  // preventDefault on touchmove); pointer events cover mouse only.
  if (event.pointerType !== 'mouse') return;
  // A control in the header (a search field, a button) owns its pointer: a drag
  // starting on it ends on the snap it started from, undoing whatever the control
  // did to `position` in between — clicking a search field that opens the sheet
  // would snap it straight back.
  if ((event.target as HTMLElement).closest('input, textarea, select, button, a')) return;
  startDrag(event.clientY);
  const onMove = (e: PointerEvent) => moveDrag(e.clientY);
  const onUp = () => {
    endDrag();
    releasePointerDrag?.();
  };
  // `pointercancel` too: the browser taking the pointer back would otherwise
  // leave the listeners on `window` and the sheet stuck mid-drag.
  releasePointerDrag = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    window.removeEventListener('pointercancel', onUp);
    releasePointerDrag = undefined;
  };
  window.addEventListener('pointermove', onMove);
  window.addEventListener('pointerup', onUp);
  window.addEventListener('pointercancel', onUp);
}

function onTouchStart(event: TouchEvent) {
  touchGesture = 'pending';
  dragStartX = event.touches[0].clientX;
  startDrag(event.touches[0].clientY);
  // Until a direction is known, don't commit: onTouchMove decides whether the
  // gesture moves the sheet or scrolls the content.
  isDragging.value = false;
}

// Whether a touch that has just declared its direction moves the sheet, rather
// than being left to the browser.
function claimsSheet(target: HTMLElement, deltaX: number, deltaY: number): boolean {
  // The footer holds the sheet's actions: dragging from it would move the
  // sheet under the finger and lose the tap.
  if (footerEl.value?.contains(target)) return false;
  if (!(contentEl.value?.contains(target) ?? false)) return true;
  // A sideways swipe belongs to a horizontal scroller in the content (the line cards).
  if (Math.abs(deltaX) > Math.abs(deltaY)) return false;
  if (contentPullsSheet.value) return true;
  // From the reading snap up the content scrolls, except a pull-down from its top.
  return deltaY > 0 && (contentEl.value?.scrollTop ?? 0) <= 0;
}

function onTouchMove(event: TouchEvent) {
  if (touchGesture === 'native') return;
  const { clientX: x, clientY: y } = event.touches[0];
  if (touchGesture === 'pending') {
    // Let the gesture declare a direction before choosing between sheet and scroll.
    const deltaX = x - dragStartX;
    const deltaY = y - dragStartY;
    if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < DIRECTION_DEAD_ZONE) return;
    const target = event.target as HTMLElement;
    if (!claimsSheet(target, deltaX, deltaY)) {
      touchGesture = 'native';
      return;
    }
    touchGesture = 'sheet';
    isDragging.value = true;
    // A pull on the content never takes the sheet past the reading snap.
    if (contentEl.value?.contains(target)) dragMaxHeight = Math.max(expandedHeight.value, sheetHeight.value);
    // Re-anchor so the sheet doesn't jump when the drag is captured mid-gesture
    dragStartY = y;
    dragStartHeight = sheetHeight.value;
  }
  if (event.cancelable) event.preventDefault();
  moveDrag(y);
}

function onTouchEnd() {
  if (touchGesture === 'sheet') endDrag();
  else isDragging.value = false;
  touchGesture = 'pending';
}
// #endregion

// #region Wheel
// Same rule as a touch pull, for a trackpad or a mouse wheel: below the reading
// snap a scroll down opens the sheet up to it, and a scroll up from the top of
// the content steps the sheet down. A trackpad keeps sending events after the
// fingers lift, so a gesture only counts as new once the stream has paused, and
// the rest of the stream is swallowed while the sheet moves.
const WHEEL_GESTURE_GAP = 150;
const WHEEL_SETTLE_DELAY = 400;

let lastWheelTime = 0;
let wheelSettleUntil = 0;

function snapBelow(from: BottomSheetPosition): BottomSheetPosition | undefined {
  const height = snapHeights.value[from];
  return [...snaps.value].reverse().find(snap => snap.height < height)?.key;
}

function onContentWheel(event: WheelEvent) {
  const now = performance.now();
  const isNewGesture = now - lastWheelTime > WHEEL_GESTURE_GAP;
  lastWheelTime = now;
  if (now < wheelSettleUntil) {
    event.preventDefault();
    return;
  }
  let target: BottomSheetPosition | undefined;
  if (event.deltaY > 0 && contentPullsSheet.value) target = 'expanded';
  else if (event.deltaY < 0 && isNewGesture && (contentEl.value?.scrollTop ?? 0) <= 0) {
    target = snapBelow(position.value);
  }
  if (!target) return;
  event.preventDefault();
  position.value = target;
  wheelSettleUntil = now + WHEEL_SETTLE_DELAY;
}
// #endregion

// Android back button: a full-screen sheet collapses instead of leaving the page.
// Registered on activation, not only on mount: the sheet lives under the
// kept-alive MapIndex, so a deactivated (but still mounted) sheet must not
// keep swallowing back presses on other pages.
let unregisterBackHandler: (() => void) | undefined;

function registerBackHandler() {
  unregisterBackHandler ??= appStore.registerBackHandler(() => {
    if (position.value === 'full') {
      position.value = 'intermediate';
      return true;
    }
    return false;
  });
}

function unregisterBackHandlerIfAny() {
  unregisterBackHandler?.();
  unregisterBackHandler = undefined;
}

let resizeObserver: ResizeObserver | undefined;

onMounted(() => {
  const parent = sheetEl.value?.parentElement;
  const measure = () => {
    if (parent) parentHeight.value = parent.getBoundingClientRect().height;
    if (headerEl.value) headerHeight.value = headerEl.value.getBoundingClientRect().height;
    if (footerEl.value) footerHeight.value = footerEl.value.getBoundingClientRect().height;
  };
  measure();
  sheetHeight.value = snapHeights.value[position.value];
  // eslint-disable-next-line compat/compat -- iOS 11 is below our real support floor (plugin-legacy targets 'defaults, not IE 11'); .browserslistrc is intentionally broader.
  resizeObserver = new ResizeObserver(measure);
  if (parent) resizeObserver.observe(parent);
  // Observed too: the header grows when its content does (a traffic-info banner
  // appearing, a long title wrapping), and the snap floor must follow.
  if (headerEl.value) resizeObserver.observe(headerEl.value);
  if (footerEl.value) resizeObserver.observe(footerEl.value);

  sheetEl.value?.addEventListener('touchstart', onTouchStart, { passive: true });
  sheetEl.value?.addEventListener('touchmove', onTouchMove, { passive: false });
  sheetEl.value?.addEventListener('touchend', onTouchEnd);
  // A gesture the system takes over (edge swipe, notification shade, incoming
  // call) only fires `touchcancel`: without it the sheet stays frozen off-snap.
  sheetEl.value?.addEventListener('touchcancel', onTouchEnd);

  registerBackHandler();
});

onActivated(registerBackHandler);
onDeactivated(unregisterBackHandlerIfAny);

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  sheetEl.value?.removeEventListener('touchstart', onTouchStart);
  sheetEl.value?.removeEventListener('touchmove', onTouchMove);
  sheetEl.value?.removeEventListener('touchend', onTouchEnd);
  sheetEl.value?.removeEventListener('touchcancel', onTouchEnd);
  releasePointerDrag?.();
  unregisterBackHandlerIfAny();
});
</script>

<template>
  <div
    ref="sheetEl"
    class="bottom-sheet"
    :class="{ 'bottom-sheet--dragging': isDragging }"
    :style="{ height: sheetHeight + 'px' }"
  >
    <div ref="headerEl" class="bottom-sheet__header" @pointerdown="onHeaderPointerDown">
      <div class="bottom-sheet__grabber-zone">
        <div class="bottom-sheet__grabber" />
      </div>
      <slot name="header" :scrolled="isContentScrolled" />
    </div>
    <div
      ref="contentEl"
      class="bottom-sheet__content"
      :class="{ 'bottom-sheet__content--pulls-sheet': contentPullsSheet }"
      @scroll.passive="onContentScroll"
      @wheel="onContentWheel"
    >
      <slot />
    </div>
    <div v-if="$slots.footer" ref="footerEl" class="bottom-sheet__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<style lang="scss">
.bottom-sheet {
  background-color: $color-white;
  border-radius: 38px 38px 0 0;
  bottom: 0;
  box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  left: 0;
  overflow: hidden;
  pointer-events: auto;
  position: absolute;
  right: 0;
  transition: height 0.3s ease;
  z-index: 2;

  &--dragging {
    transition: none;
  }

  &__header {
    flex-shrink: 0;
    touch-action: none;
  }

  &__grabber-zone {
    display: flex;
    justify-content: center;
    padding: 6px 0 2px;
  }

  &__grabber {
    background-color: $color-neutral-medium;
    border-radius: 100px;
    height: 5px;
    width: 36px;
  }

  &__content {
    flex: 1 1 auto;
    overflow-y: auto;
    overscroll-behavior: contain;

    // Vertical pans are the sheet's below the reading snap; horizontal ones stay
    // native for the scrollers inside (the line cards).
    &--pulls-sheet {
      touch-action: pan-x;
    }
  }

  &__footer {
    flex-shrink: 0;
  }
}
</style>
